import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { ChevronDown, Check } from "lucide-react";
import { dialCodeList, dialCode } from "@/lib/countries";
import { PhoneCheckHint, usePhoneCheck } from "@/components/PhoneCheckHint";

// ISO2 → emoji flag via regional indicator symbols.
export const flagEmoji = (iso2) => {
  if (!iso2 || iso2.length !== 2) return "🏳️";
  const base = 127397; // 0x1F1E6 - 'A'.charCodeAt(0)
  return String.fromCodePoint(...iso2.toUpperCase().split("").map((c) => base + c.charCodeAt(0)));
};

// Numéro national → E.164. Le « 0 » de tête est le préfixe national (0492… en Belgique, 06… en France, 081… en RDC) :
// il ne fait pas partie du numéro international → retiré, SAUF dans les pays où ce 0 est significatif (lot 632 : Italie,
// Congo-Brazzaville +242 06x…, Côte d'Ivoire +225 07…, Bénin +229 01…, Gabon +241 06…). Un indicatif recopié dans la
// partie nationale (+32 32492… ou « 32492… ») est dédoublonné.
export const KEEP_TRUNK_ZERO = new Set(["IT", "CG", "CI", "BJ", "GA"]);
export const toE164 = (iso, national) => {
  const dial = dialCode(iso);
  let nat = (national || "").replace(/[^\d]/g, "");
  if (!nat) return "";
  if (dial && nat.startsWith(dial) && nat.length > dial.length + 7) nat = nat.slice(dial.length);
  if (!KEEP_TRUNK_ZERO.has(iso)) nat = nat.replace(/^0+/, "");
  return nat ? `+${dial}${nat}` : "";
};

// Sélecteur de pays (drapeau + indicatif) partagé par PhoneField et le champ unique de connexion.
export const DialCodePicker = ({ iso, onSelect, lang, testId, className = "" }) => {
  const { t } = useApp();
  const list = useMemo(() => dialCodeList(lang), [lang]);
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex items-center gap-1.5 shrink-0 h-10 px-3 rounded-md border bg-transparent text-sm ${className}`}
          style={{ borderColor: "var(--yalola-border)" }}
          data-testid={`${testId}-country`}
        >
          <span className="text-base leading-none">{flagEmoji(iso)}</span>
          <span className="font-medium">+{dialCode(iso)}</span>
          <ChevronDown size={15} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[290px]" align="start" data-testid={`${testId}-country-popover`}>
        <Command
          filter={(itemValue, search) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            // strip diacritics so "cote" matches "Côte d'Ivoire"
            const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
            return norm(itemValue).includes(norm(q)) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={t("phone.search_country", "Rechercher un pays ou un indicatif…")} data-testid={`${testId}-country-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>{t("phone.no_country", "Aucun pays trouvé")}</CommandEmpty>
            {list.map((c) => (
              <CommandItem
                key={c.code}
                value={`${c.label} ${c.code} +${c.dial}`}
                onSelect={() => { onSelect(c.code); setOpen(false); }}
                className="flex items-center gap-2 cursor-pointer"
                data-testid={`${testId}-country-${c.code}`}
              >
                <span className="text-base leading-none">{flagEmoji(c.code)}</span>
                <span className="font-medium w-14 shrink-0">{c.code} +{c.dial}</span>
                <span className="flex-1 min-w-0 truncate" style={{ color: "var(--yalola-text-muted)" }}>{c.label}</span>
                {iso === c.code && <Check size={15} style={{ color: "var(--yalola-primary)" }} />}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Controlled international phone input: searchable country dial-code picker + national number.
// value = { iso, national, e164 } ; onChange receives the same shape (e164 recomputed).
// Lot 560 — vérifie en direct la longueur du numéro pour le pays (hint sous le champ) ; onCheck(check) pour bloquer l'envoi.
export const PhoneField = ({ value, onChange, lang, testId, defaultIso = "CD", onCheck }) => {
  const iso = value?.iso || defaultIso;
  const national = value?.national || "";
  const check = usePhoneCheck(national ? toE164(iso, national) : "", iso);
  const [focused, setFocused] = useState(false);
  useEffect(() => { onCheck?.(check); }, [check, onCheck]);

  const emit = (nextIso, nextNat) => {
    const nat = (nextNat || "").replace(/[^\d]/g, "");
    onChange({ iso: nextIso, national: nat, e164: toE164(nextIso, nat) });
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2" data-testid={testId}>
        <DialCodePicker iso={iso} onSelect={(code) => emit(code, national)} lang={lang} testId={testId} />
        <Input
          type="tel"
          inputMode="numeric"
          value={national}
          onChange={(e) => emit(iso, e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="812 345 678"
          className="flex-1"
          data-testid={`${testId}-number`}
        />
      </div>
      <PhoneCheckHint check={check} lang={lang} testId={testId} showShort={!focused} />
    </div>
  );
};

export default PhoneField;
